import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { apiErrorMessage } from '@/lib/api-error';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

export interface FiscalSettingsValue {
  mode: 'mock' | 'real';
  seller_inn: string;
  terminal_id: string;
  vat_percent: 0 | 12;
  z_report_roles: string[];
}

const DEFAULTS: FiscalSettingsValue = { mode: 'mock', seller_inn: '', terminal_id: '', vat_percent: 0, z_report_roles: ['owner'] };
const ROLES = ['owner', 'manager', 'cashier'] as const;
const SETTING_KEY = 'fiscal_settings';

/** FR-105: «Fiskal kassa» settings — platform owner only (the API enforces it too). */
export function FiscalSettingsCard({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<FiscalSettingsValue>(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['/admin/platform-settings/{key}', { params: { path: { key: SETTING_KEY } } }] });

  useEffect(() => {
    if (data?.data) setForm({ ...DEFAULTS, ...((data.data.value ?? {}) as Partial<FiscalSettingsValue>) });
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await apiClient.PUT('/admin/platform-settings/{key}', { params: { path: { key: SETTING_KEY } }, body: { value: form } });
      if (error) throw error;
      toast.success(t('fiscal.settings.saved'));
      qc.invalidateQueries({ queryKey: ['/admin/platform-settings/{key}'] });
    } catch (e) {
      toast.error(apiErrorMessage(e, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (role: string, on: boolean) =>
    setForm((f) => ({ ...f, z_report_roles: on ? [...new Set([...f.z_report_roles, role])] : f.z_report_roles.filter((r) => r !== role) }));

  return (
    <Card data-testid="fiscal-settings">
      <CardHeader><CardTitle className="text-base">{t('fiscal.settings.title')}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="py-6 text-center text-muted-foreground"><Loader2 className="inline animate-spin" size={16} /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">{t('fiscal.settings.mode')}</Label>
              <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v as 'mock' | 'real' }))} disabled={!canEdit}>
                <SelectTrigger aria-label={t('fiscal.settings.mode')}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mock">{t('fiscal.settings.mode_mock')}</SelectItem>
                  <SelectItem value="real">{t('fiscal.settings.mode_real')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">{t('fiscal.settings.vat')}</Label>
              <Select value={String(form.vat_percent)} onValueChange={(v) => setForm((f) => ({ ...f, vat_percent: Number(v) as 0 | 12 }))} disabled={!canEdit}>
                <SelectTrigger aria-label={t('fiscal.settings.vat')}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0%</SelectItem>
                  <SelectItem value="12">12%</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">{t('fiscal.settings.seller_inn')}</Label>
              <Input value={form.seller_inn} onChange={(e) => setForm((f) => ({ ...f, seller_inn: e.target.value }))} disabled={!canEdit} className="font-mono" inputMode="numeric" maxLength={9} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">{t('fiscal.settings.terminal_id')}</Label>
              <Input value={form.terminal_id} onChange={(e) => setForm((f) => ({ ...f, terminal_id: e.target.value }))} disabled={!canEdit} className="font-mono" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-muted-foreground mb-1 block">{t('fiscal.settings.z_report_roles')}</Label>
              <div className="flex flex-wrap gap-4">
                {ROLES.map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.z_report_roles.includes(r)} onCheckedChange={(v) => toggleRole(r, v === true)} disabled={!canEdit} />
                    {t(`fiscal.settings.role.${r}`)}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{t('fiscal.settings.z_hint')}</p>
            </div>
          </div>
        )}
        {canEdit ? (
          <Button onClick={save} disabled={saving || isLoading}>
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} {t('fiscal.settings.save')}
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">{t('fiscal.settings.owner_only')}</p>
        )}
      </CardContent>
    </Card>
  );
}
